/**
 * Add funds (the design system's `DepositModal`): one open state for the whole app, so the
 * header's Deposit and balance, the account header and every "Buy {unit}" link open the same
 * modal over whatever page the shopper is on; and what a URL asks of it. The modal itself is
 * `components/StoreDeposit.tsx`, its content `lib/depositContent.ts`.
 */
import { useLocation } from "@tanstack/react-router";
import { useSyncExternalStore } from "react";

const listeners = new Set<() => void>();
let depositOpen = false;
const openStore = {
  get: () => depositOpen,
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

/** Opens or closes Add funds. It starts closed on every page load unless the URL asks for it. */
export function setDepositOpen(open: boolean): void {
  if (depositOpen === open) return;
  depositOpen = open;
  for (const listener of listeners) listener();
}

/** Whether Add funds is open. */
export function useDepositOpen(): boolean {
  return useSyncExternalStore(openStore.subscribe, openStore.get, openStore.get);
}

/** `?deposit=1` on any page opens Add funds over it: the deep link, and every "Buy" link. */
export const DEPOSIT_PARAM = "deposit";

/** The search keys Stripe's checkout return carries (`core/stripe/balancePurchases.ts`). */
const PURCHASE_PARAM = "purchase";
const SESSION_PARAM = "session_id";

/** A checkout coming back from Stripe: paid (to confirm by polling), or canceled. */
export type CheckoutReturn = { status: "success"; sessionId: string } | { status: "cancelled" };

/** What a URL asks of Add funds; null when it asks nothing. */
export interface DepositRequest {
  /** Stripe's return, when the URL is one. */
  checkout: CheckoutReturn | null;
}

/**
 * Reads what `searchStr` asks of Add funds: `?deposit=1` on any page (the Account page's old
 * Wallet tab, `?tab=wallet`, is rewritten to it by `parseAccountSearch`), or Stripe's return
 * (`/account?purchase=success&session_id=…` or `?purchase=cancelled`).
 */
export function depositRequestOf(searchStr: string): DepositRequest | null {
  const search = new URLSearchParams(searchStr);
  const purchase = search.get(PURCHASE_PARAM);
  const sessionId = search.get(SESSION_PARAM);
  if (purchase === "success" && sessionId) {
    return { checkout: { status: "success", sessionId } };
  }
  if (purchase === "cancelled") return { checkout: { status: "cancelled" } };
  // A success with no session id is nothing to confirm; it still lands on the modal.
  if (purchase || search.has(DEPOSIT_PARAM)) return { checkout: null };
  return null;
}

/** The URL with what `depositRequestOf` read taken out, so a reload or Back doesn't repeat it. */
export function withoutDepositRequest(pathname: string, searchStr: string): string {
  const search = new URLSearchParams(searchStr);
  for (const key of [DEPOSIT_PARAM, PURCHASE_PARAM, SESSION_PARAM]) search.delete(key);
  const rest = search.toString();
  return rest ? `${pathname}?${rest}` : pathname;
}

/** The current page with `?deposit=1` added: a "Buy {unit}" link that opens Add funds over it. */
export function depositHref(pathname: string, searchStr: string): string {
  const search = new URLSearchParams(searchStr);
  search.set(DEPOSIT_PARAM, "1");
  return `${pathname}?${search.toString()}`;
}

/** `depositHref` for the page showing: where a "Buy {unit}" link points. */
export function useDepositHref(): string {
  const pathname = useLocation({ select: (location) => location.pathname });
  const searchStr = useLocation({ select: (location) => location.searchStr });
  return depositHref(pathname, searchStr);
}
